// Mouse, keyboard and touch controls. The game loop reads them once per frame with poll().
// Desktop: mouse steers, Space splits, hold W to feed, hold Right-click or Shift to boost, Esc for the lobby.
// Phones/tablets: drag anywhere for a floating joystick; Split, Feed and Boost buttons sit bottom-right,
// and a small × at the top centre leaves the game (the touch Esc).

export interface InputState {
  ax: number; ay: number; // aim in screen px from the screen centre (0, 0 = stand still)
  split: boolean; // pressed since the last poll()
  eject: boolean; // held
  boost: boolean; // held
}

const JOY_R = 56; // joystick travel, CSS px
const JOY_DEAD = 6; // drag (px) before the joystick takes a new direction
const coarse = () => typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
const typing = (t: EventTarget | null) => {
  const tag = (t as { tagName?: string } | null)?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
};

export class Input {
  touchMode = coarse();
  // last direction the joystick moved (unit vector): with an idle joystick, split and feed aim along it
  readonly lastDir = { x: 1, y: 0 };
  onEscape: (() => void) | null = null;

  private active = false;
  private mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  private joy = { id: null as number | null, ox: 0, oy: 0, dx: 0, dy: 0 }; // dx/dy = deflection -1..1
  private splitLatch = false;
  // held controls; eject and boost are resolved from them in poll()
  private held = { boostMouse: false, boostKey: false, boostTouch: false, ejectKey: false, ejectTouch: false };
  private readonly ac = new AbortController();
  private readonly touchUI = document.getElementById('touch');
  private readonly stick = document.getElementById('stick');
  private readonly knob = document.getElementById('knob');
  private readonly buttons: HTMLElement[] = [];

  constructor(canvas: HTMLCanvasElement) {
    const on = { signal: this.ac.signal };
    const opt = { signal: this.ac.signal, passive: false };
    // ---- mouse + keyboard ----
    window.addEventListener('contextmenu', e => { if (this.active) e.preventDefault(); }, opt);
    window.addEventListener('mousedown', e => { if (e.button === 2 && this.active) this.held.boostMouse = true; }, on);
    window.addEventListener('mouseup', e => { if (e.button === 2) this.held.boostMouse = false; }, on);
    window.addEventListener('mousemove', e => {
      this.mouse.x = e.clientX; this.mouse.y = e.clientY;
      if (this.held.boostMouse && !(e.buttons & 2)) this.held.boostMouse = false; // released outside the window
    }, on);
    // focus or the page went away: nothing stays held (keyups and pointerups may never arrive)
    window.addEventListener('blur', () => this.releaseAll(), on);
    window.addEventListener('pagehide', () => this.releaseAll(), on);
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.releaseAll(); }, on);
    window.addEventListener('keydown', e => this.keyDown(e), opt);
    window.addEventListener('keyup', e => {
      if (e.code === 'KeyW') this.held.ejectKey = false;
      if (e.key === 'Shift') this.held.boostKey = false;
    }, on);
    // ---- touch: floating joystick on the canvas ----
    canvas.addEventListener('pointerdown', e => {
      if (e.pointerType === 'mouse') return;
      this.setTouchMode(true);
      if (this.joy.id !== null || !this.active) return;
      const j = this.joy;
      j.id = e.pointerId; j.ox = e.clientX; j.oy = e.clientY; j.dx = j.dy = 0;
      try { canvas.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
      if (this.stick) { this.stick.style.left = `${e.clientX}px`; this.stick.style.top = `${e.clientY}px`; this.stick.classList.add('on'); }
      if (this.knob) this.knob.style.transform = '';
    }, on);
    canvas.addEventListener('pointermove', e => {
      if (e.pointerId !== this.joy.id) return;
      let dx = e.clientX - this.joy.ox, dy = e.clientY - this.joy.oy;
      const d = Math.hypot(dx, dy);
      if (d > JOY_R) { dx *= JOY_R / d; dy *= JOY_R / d; }
      if (d > JOY_DEAD) { const l = Math.hypot(dx, dy); this.lastDir.x = dx / l; this.lastDir.y = dy / l; }
      this.joy.dx = dx / JOY_R; this.joy.dy = dy / JOY_R;
      if (this.knob) this.knob.style.transform = `translate(${dx}px, ${dy}px)`;
    }, on);
    canvas.addEventListener('pointerup', e => this.endJoy(e.pointerId), on);
    canvas.addEventListener('pointercancel', e => this.endJoy(e.pointerId), on);
    // a real mouse on a non-touch screen switches back to mouse steering
    window.addEventListener('pointermove', e => {
      if (e.pointerType === 'mouse' && this.touchMode && !coarse()) this.setTouchMode(false);
    }, on);
    // iOS Safari ignores user-scalable=no; stop pinch-zoom during play
    document.addEventListener('gesturestart', e => e.preventDefault(), opt);
    // ---- touch: hold buttons ----
    this.hold('tSplit', () => { this.splitLatch = true; });
    this.hold('tEject', () => { this.held.ejectTouch = true; }, () => { this.held.ejectTouch = false; });
    this.hold('tBoost', () => { this.held.boostTouch = true; }, () => { this.held.boostTouch = false; });
    // ---- touch: exit to the lobby ----
    document.getElementById('tExit')?.addEventListener('click', e => {
      e.preventDefault();
      if (this.active) this.onEscape?.();
    }, on);
    this.refreshTouchUI();
  }

  // true while the touch joystick is released: split/feed should fire along lastDir, not in place
  get idle(): boolean { return this.touchMode && this.joy.id === null; }

  poll(): InputState {
    const W = window.innerWidth, H = window.innerHeight, h = this.held;
    let ax: number, ay: number;
    if (this.touchMode) {
      // joystick deflection sets direction and speed; letting go stops the blob
      const reach = Math.min(W, H) * 0.45;
      ax = this.joy.dx * reach; ay = this.joy.dy * reach;
    } else {
      ax = this.mouse.x - W / 2; ay = this.mouse.y - H / 2;
    }
    const split = this.splitLatch;
    this.splitLatch = false;
    return { ax, ay, split, eject: h.ejectKey || h.ejectTouch, boost: h.boostMouse || h.boostKey || h.boostTouch };
  }

  // in a game (controls live, touch UI shown) or in a menu (keys ignored so text fields work)
  setActive(active: boolean): void {
    this.active = active;
    this.reset();
    this.refreshTouchUI();
  }

  // drops every held control (window blur, tab hidden, game start/end)
  reset(): void {
    const h = this.held;
    h.boostMouse = h.boostKey = h.boostTouch = h.ejectKey = h.ejectTouch = false;
    this.splitLatch = false;
    for (const b of this.buttons) b.classList.remove('on');
  }

  // reset() plus the joystick: a finger that was down when the tab went away doesn't keep steering
  releaseAll(): void {
    this.reset();
    this.endJoy(null);
  }

  destroy(): void {
    this.ac.abort();
    this.setActive(false);
  }

  private keyDown(e: KeyboardEvent): void {
    if (!this.active || typing(e.target)) return;
    if (e.code === 'Space') { e.preventDefault(); if (!e.repeat) this.splitLatch = true; }
    else if (e.code === 'KeyW') this.held.ejectKey = true;
    else if (e.key === 'Shift') this.held.boostKey = true;
    else if (e.code === 'Escape') this.onEscape?.();
  }

  private setTouchMode(on: boolean): void {
    if (this.touchMode !== on) { this.touchMode = on; this.refreshTouchUI(); }
  }
  private refreshTouchUI(): void {
    document.body?.classList.toggle('touch', this.touchMode);
    this.touchUI?.classList.toggle('hidden', !(this.touchMode && this.active));
    if (!this.active) this.endJoy(null);
  }
  private endJoy(pointerId: number | null): void {
    if (pointerId !== null && pointerId !== this.joy.id) return;
    this.joy.id = null; this.joy.dx = this.joy.dy = 0;
    this.stick?.classList.remove('on');
  }
  private hold(id: string, down: () => void, up?: () => void): void {
    const el = document.getElementById(id);
    if (!el) return;
    this.buttons.push(el);
    const on = { signal: this.ac.signal };
    el.addEventListener('pointerdown', e => {
      e.preventDefault();
      try { el.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
      el.classList.add('on');
      if (this.active) down();
    }, { signal: this.ac.signal, passive: false });
    const release = () => {
      if (!el.classList.contains('on')) return;
      el.classList.remove('on');
      up?.();
    };
    el.addEventListener('pointerup', release, on);
    el.addEventListener('pointercancel', release, on);
    el.addEventListener('lostpointercapture', release, on);
    el.addEventListener('contextmenu', e => e.preventDefault(), on);
  }
}
